import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { unparse } from 'papaparse';
import { toast } from 'sonner';

import {
  formatPurchaseProofDate,
  usePurchaseProofsFilterFields,
  usePurchaseProofsColumns
} from '@admin/components/purchase-proofs/purchase-proofs-columns.tsx';
import {
  apiAdminpurchaseProofsGetCollection,
  getApiAdminpurchaseProofsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  ApiAdminpurchaseProofsGetCollectionParams,
  PurchaseProofPurchaseProofCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { Button } from '@ui/button.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { downloadCSV } from '@r/utils/utils.ts';
import { buildPurchaseProofsParams } from './purchase-proofs-query.ts';

const MAX_PAGE_SIZE = 30;

/** Displays and exports the workspace purchase proofs available to administrators. */
export function PurchaseProofs() {
  const { t } = useTranslation();
  const user = useUser();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const [isExporting, setIsExporting] = useState(false);
  const [purchaseProofs, setPurchaseProofs] = useState<
    PurchaseProofPurchaseProofCollectionReadModel[]
  >([]);
  const [pageCount, setPageCount] = useState(1);

  const persistenceKey = useMemo(
    () => getAdminTablePersistenceKey('purchase-proofs', user.data?.id),
    [user.data?.id]
  );
  const filterFields = usePurchaseProofsFilterFields();

  const { table } = useDataTable({
    data: purchaseProofs,
    columns: usePurchaseProofsColumns(),
    pageCount,
    filterFields,
    enableAdvancedFilter: true,
    persistence: { key: persistenceKey }
  });

  const { pageIndex, pageSize } = table.getState().pagination as PaginationState;
  const effectivePageSize = Math.min(pageSize, MAX_PAGE_SIZE);
  const params: ApiAdminpurchaseProofsGetCollectionParams = buildPurchaseProofsParams({
    columnFilters: table.getState().columnFilters,
    sorting: table.getState().sorting,
    page: pageIndex + 1,
    perPage: effectivePageSize
  });

  const purchaseProofsQuery = useQuery({
    queryKey: getApiAdminpurchaseProofsGetCollectionQueryKey(params),
    queryFn: async () => {
      try {
        return await apiAdminpurchaseProofsGetCollection(params);
      } catch (error) {
        if (isOrvalErrorFromPath(error, 'get', '/admin/purchase_proofs')) {
          showOrvalErrorToast(error, t('toast.error.label'));
        } else {
          toast.error(t('toast.error.label'));
        }
        throw error;
      }
    },
    placeholderData: (previousData) => previousData
  });

  useEffect(() => {
    if (purchaseProofsQuery.data) {
      setPurchaseProofs(purchaseProofsQuery.data.data ?? []);
      setPageCount(purchaseProofsQuery.data.pagination?.last ?? 1);
    }
  }, [purchaseProofsQuery.data]);

  useEffect(() => {
    if (pageSize > MAX_PAGE_SIZE) {
      table.setPageSize(MAX_PAGE_SIZE);
    }
  }, [pageSize, table]);

  /** Fetches every matching page and downloads the resulting purchase proofs as CSV. */
  async function exportData() {
    setIsExporting(true);

    try {
      const totalItems = purchaseProofsQuery.data?.pagination?.totalItems ?? 0;
      const totalPages = Math.ceil(totalItems / MAX_PAGE_SIZE);
      const pages = await Promise.all(
        Array.from({ length: totalPages }, (_, page) =>
          apiAdminpurchaseProofsGetCollection({
            ...params,
            page: page + 1,
            perPage: MAX_PAGE_SIZE
          })
        )
      );
      const purchaseProofs = pages.flatMap((page) => page.data ?? []);
      const csv = unparse({
        fields: [
          t('label.id'),
          t('label.purchase.proof.filename'),
          t('label.purchase.proof.comment'),
          t('label.purchase.proof.created.by'),
          t('label.purchase.proof.company'),
          t('label.purchase.proof.created.at'),
          t('label.purchase.proof.status')
        ],
        data: purchaseProofs.map((proof) => [
          proof.id,
          proof.filename,
          proof.comment ?? '',
          proof.createdBy?.user.fullname ?? '',
          proof.createdBy?.company?.name ?? '',
          formatPurchaseProofDate(proof.createdAt),
          t(`purchase.proof.status.${proof.status}`)
        ])
      });
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const baseFilename = `justificatifs-achats-${timestamp}.csv`;

      downloadCSV(
        csv,
        sanitizedWorkspaceName ? `${sanitizedWorkspaceName}_${baseFilename}` : baseFilename
      );
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/purchase_proofs')) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(t('toast.error.label'));
      }
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.purchase.proofs.title')}</span>

      {purchaseProofsQuery.isLoading && (
        <DataTableSkeleton
          columnCount={7}
          cellWidths={['5rem', '24rem', '20rem', '14rem', '14rem', '12rem', '10rem']}
        />
      )}

      {purchaseProofsQuery.isError && (
        <div className="flex flex-col gap-4 items-center p-8 text-center rounded-lg border border-dashed">
          <p className="text-muted-foreground">{t('admin.purchase.proofs.list.error')}</p>
          <Button variant="outline" onClick={() => void purchaseProofsQuery.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {purchaseProofsQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />
          <DataTable table={table} totalItems={purchaseProofsQuery.data.pagination?.totalItems} />
        </>
      )}
    </>
  );
}
